import { Component, createRef, RefObject } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './color-blindness-simulator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

// Color-blindness simulation matrices (Brettel / Machado / Vischeck approximations).
// Each 3x3 maps an RGB triple to its perceived equivalent for the given vision type.
const MATRICES: Record<string, [number, number, number, number, number, number, number, number, number]> = {
    'normal': [1, 0, 0, 0, 1, 0, 0, 0, 1],
    'protanopia': [0.567, 0.433, 0, 0.558, 0.442, 0, 0, 0.242, 0.758],
    'protanomaly': [0.817, 0.183, 0, 0.333, 0.667, 0, 0, 0.125, 0.875],
    'deuteranopia': [0.625, 0.375, 0, 0.7, 0.3, 0, 0, 0.3, 0.7],
    'deuteranomaly': [0.8, 0.2, 0, 0.258, 0.742, 0, 0, 0.142, 0.858],
    'tritanopia': [0.95, 0.05, 0, 0, 0.433, 0.567, 0, 0.475, 0.525],
    'tritanomaly': [0.967, 0.033, 0, 0, 0.733, 0.267, 0, 0.183, 0.817],
    'achromatopsia': [0.299, 0.587, 0.114, 0.299, 0.587, 0.114, 0.299, 0.587, 0.114],
    'achromatomaly': [0.618, 0.320, 0.062, 0.163, 0.775, 0.062, 0.163, 0.320, 0.516],
};

type VisionType = keyof typeof MATRICES;

const ALL_TYPES: VisionType[] = ['normal', 'protanopia', 'protanomaly', 'deuteranopia', 'deuteranomaly', 'tritanopia', 'tritanomaly', 'achromatopsia', 'achromatomaly'];

interface State {
    fileName: string;
    imageUrl: string | null;
}

export default class ColorBlindnessSimulatorComponent extends Component<{ feature?: string }, State> {
    private canvasRefs: Record<string, RefObject<HTMLCanvasElement | null>> = {};
    private imgRef: RefObject<HTMLImageElement | null> = createRef();
    state: State = { fileName: '', imageUrl: null };

    constructor(props: { feature?: string }) {
        super(props);
        ALL_TYPES.forEach(t => { this.canvasRefs[t] = createRef(); });
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        this.setState({ imageUrl: url, fileName: f.name });
    };

    private applyMatrix(data: Uint8ClampedArray, matrix: typeof MATRICES['normal']): void {
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            data[i] = Math.min(255, Math.max(0, Math.round(matrix[0] * r + matrix[1] * g + matrix[2] * b)));
            data[i + 1] = Math.min(255, Math.max(0, Math.round(matrix[3] * r + matrix[4] * g + matrix[5] * b)));
            data[i + 2] = Math.min(255, Math.max(0, Math.round(matrix[6] * r + matrix[7] * g + matrix[8] * b)));
        }
    }

    onImageLoad = () => {
        if (!this.imgRef.current) return;
        const img = this.imgRef.current;
        const maxDim = 320;
        const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.floor(img.naturalWidth * scale);
        const h = Math.floor(img.naturalHeight * scale);
        for (const type of ALL_TYPES) {
            const canvas = this.canvasRefs[type].current;
            if (!canvas) continue;
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
            ctx.drawImage(img, 0, 0, w, h);
            if (type !== 'normal') {
                const imageData = ctx.getImageData(0, 0, w, h);
                this.applyMatrix(imageData.data, MATRICES[type]);
                ctx.putImageData(imageData, 0, 0);
            }
        }
    };

    render() {
        const { fileName, imageUrl } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Image (runs locally)</label>
                        <div className={styles.fileBox}>
                            <input type="file" id="cb-file" accept="image/*" className={styles.fileInput}
                                onChange={this.onFileChange} />
                            <label htmlFor="cb-file" className={sharedStyles.buttonPrimary}>Choose image</label>
                            {fileName && <div className={styles.fileName}>{fileName}</div>}
                        </div>
                        <p className={styles.note}>
                            Simulates the 8 most common color-vision deficiencies using standard transformation matrices.
                            Use these to check that your design is legible to viewers with color blindness (~8% of men, 0.5% of women).
                        </p>
                        {imageUrl && (
                            <>
                                <label className={sharedStyles.label}>Original</label>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img ref={this.imgRef} src={imageUrl} alt="Source" onLoad={this.onImageLoad}
                                    className={styles.original} />
                            </>
                        )}
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Simulations</label>
                        <div className={styles.grid}>
                            {ALL_TYPES.filter(t => t !== 'normal').map(t => (
                                <div key={t} className={styles.cell}>
                                    <canvas ref={this.canvasRefs[t]} className={styles.canvas} />
                                    <span className={styles.cellLabel}>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
